import { useTranslation } from 'react-i18next';
import type { Invoice } from '@hero/schema';
import { Price } from '@hero/web-components';
import {
  AlertCircleIcon,
  ArrowRightIcon,
  CalendarIcon,
  CircleCheckIcon,
  FileTextIcon,
  ReceiptIcon,
} from '@hero/web-icons';
import { heroInvoiceProofChecks, isHeroInvoiceProofComplete } from '../Requirements';

export interface HeroInvoiceProofCardProps {
  /**
   * The picked Hero invoice (or `undefined` while the
   * `useInvoiceApi` fetch is in flight). When `undefined` the card
   * renders a neutral "loading" surface — `null` is reserved for
   * "no invoice picked" and the caller hides the card entirely in
   * that case.
   */
  invoice: Invoice | undefined;
  /** Opens the edit drawer pre-filled with the current values. */
  onEdit: () => void;
}

/**
 * `HeroInvoiceProofCard` — Single consolidated "Invoice details"
 * card for the financing apply wizard's Hero-invoice tab.
 *
 * Renders as a clickable `<button>` row, matching the
 * `RequirementsCard` chrome that the Signed-contract /
 * Customer-business cards use — the whole card is the click target,
 * with a top-right text-style CTA (`Edit details →`) aligned with
 * the title row. Below the header, a 2x2 grid surfaces the four
 * lender-relevant fields:
 *
 *  1. **Amount**           — read-only, always from `invoice.amount`.
 *  2. **Expected due date** — `invoice.period.due` (client pay date).
 *                            Editable via the `UploadInvoiceProofDrawer`.
 *  3. **Invoice number**    — `invoice.invoiceProof.refNumber`.
 *                            Empty until the seller types their own
 *                            accounting ref.
 *  4. **VAT PDF**           — `invoice.invoiceProof.image`. Empty
 *                            until the seller uploads the actual
 *                            PDF from their books.
 *
 * Card tone (emerald / red) follows `isHeroInvoiceProofComplete` —
 * the same predicate that gates the wizard's submit, so the user
 * never sees a green card with a disabled Continue (or vice versa).
 */
export function HeroInvoiceProofCard({ invoice, onEdit }: HeroInvoiceProofCardProps) {
  const { t } = useTranslation('m-financing');

  // While we're still resolving the invoice doc, show a neutral
  // skeleton-ish shell so the card has presence on the page but
  // doesn't flash red "missing" badges before the data lands.
  if (!invoice) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        <div className="flex items-center gap-3">
          <div className="size-9 animate-pulse rounded-full bg-gray-100" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 animate-pulse rounded bg-gray-100" />
            <div className="h-3 w-48 animate-pulse rounded bg-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  const checks = heroInvoiceProofChecks(invoice);
  const complete = isHeroInvoiceProofComplete(invoice);
  const styles = complete ? TONE_OK : TONE_BLOCK;

  const amountValue = invoice.amount?.remaining ?? invoice.amount?.total ?? 0;
  const currency = invoice.amount?.currency ?? 'SAR';

  const dueDate = invoice.period?.due
    ? new Date(invoice.period.due).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const sellerRef = invoice.invoiceProof?.refNumber?.trim() || null;

  // Item shell — one per field. Renders an icon, the field name,
  // and either the value (green check) or the "missing" copy (red
  // dot). Kept inline because it's tightly coupled to this card's
  // grid + tone language.
  const items: Array<{
    key: keyof typeof checks | 'amount';
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    complete: boolean;
  }> = [
    {
      key: 'amount',
      icon: <ReceiptIcon className="size-3.5" />,
      label: t('apply_form.invoice.hero.details_card.amount_label'),
      value: (
        <Price
          amount={amountValue}
          currency={currency}
          className="text-sm font-semibold text-gray-900"
        />
      ),
      complete: true,
    },
    {
      key: 'expectedDueDate',
      icon: <CalendarIcon className="size-3.5" />,
      label: t('apply_form.invoice.hero.details_card.due_label'),
      value: dueDate ? (
        <span className="text-sm font-medium text-gray-900">{dueDate}</span>
      ) : (
        <span className="text-xs text-red-700">
          {t('apply_form.invoice.hero.details_card.missing.due')}
        </span>
      ),
      complete: checks.expectedDueDate,
    },
    {
      key: 'invoiceNumber',
      // No dedicated hash icon in `@hero/web-icons` — use the
      // glyph itself in a small chip. Reads visually identical to
      // the other field icons (square w/ a single character) and
      // matches the `#` adornment in `InvoiceNumberInput`.
      icon: <span className="text-[11px] font-semibold leading-none">#</span>,
      label: t('apply_form.invoice.hero.details_card.number_label'),
      value: sellerRef ? (
        <span className="font-mono text-sm font-medium text-gray-900">#{sellerRef}</span>
      ) : (
        <span className="text-xs text-red-700">
          {t('apply_form.invoice.hero.details_card.missing.number')}
        </span>
      ),
      complete: checks.invoiceNumber,
    },
    {
      key: 'vatFile',
      icon: <FileTextIcon className="size-3.5" />,
      label: t('apply_form.invoice.hero.details_card.vat_label'),
      value: checks.vatFile ? (
        <span className="text-sm font-medium text-emerald-700">
          {t('apply_form.invoice.hero.details_card.vat_uploaded')}
        </span>
      ) : (
        <span className="text-xs text-red-700">
          {t('apply_form.invoice.hero.details_card.missing.vat')}
        </span>
      ),
      complete: checks.vatFile,
    },
  ];

  return (
    <button
      type="button"
      onClick={onEdit}
      className={[
        'group flex w-full items-start gap-3 rounded-2xl border p-4 text-start transition',
        'hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40',
        styles.surface,
      ].join(' ')}>
      <span
        className={[
          'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full',
          styles.iconBg,
        ].join(' ')}>
        {complete ? (
          <CircleCheckIcon className={['size-5', styles.iconColor].join(' ')} />
        ) : (
          <AlertCircleIcon className={['size-5', styles.iconColor].join(' ')} />
        )}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h4 className="truncate text-sm font-semibold text-gray-900">
                {t('apply_form.invoice.hero.details_card.title')}
              </h4>
              {complete ? (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                  {t('requirements.complete_badge')}
                </span>
              ) : (
                <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                  {t('requirements.required_badge')}
                </span>
              )}
            </div>
            <p className="mt-0.5 text-xs text-gray-600">
              {t('apply_form.invoice.hero.details_card.description')}
            </p>
          </div>

          {/*
           * Header CTA — top-right, aligned with the title row.
           * Matches `RequirementsCard` ("Open contract →") so the
           * action doesn't float beside the field grid below.
           */}
          <span
            className={[
              'flex shrink-0 items-center gap-1 text-xs font-semibold',
              styles.ctaColor,
            ].join(' ')}>
            {t('apply_form.invoice.hero.details_card.edit_action')}
            <ArrowRightIcon className="size-3.5 rtl:rotate-180" />
          </span>
        </div>

        {/*
         * Field grid — `<dl>` is flow content, allowed inside
         * `<button>` (and matches the `<ul>` of "missing items"
         * `RequirementsCard` already nests). No nested
         * interactive elements, so the whole card stays a single
         * click target.
         */}
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
          {items.map(item => (
            <div
              key={item.key}
              className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 bg-white/70 px-3 py-2">
              <div className="flex min-w-0 items-center gap-2 text-xs text-gray-500">
                <span
                  className={[
                    'flex size-6 shrink-0 items-center justify-center rounded-md',
                    item.complete ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500',
                  ].join(' ')}>
                  {item.icon}
                </span>
                <dt className="truncate">{item.label}</dt>
              </div>
              <dd className="flex shrink-0 items-center gap-1.5">
                {item.complete ? (
                  <CircleCheckIcon className="size-3.5 text-emerald-600" />
                ) : (
                  <span className="size-1.5 rounded-full bg-red-500" />
                )}
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </button>
  );
}

/**
 * Tone palette — keyed on whether the card is satisfied. Mirrors
 * the green / red surface tones used by `RequirementsCard` so the
 * Hero invoice details card visually fits next to it.
 */
const TONE_OK = {
  surface: 'border-emerald-200 bg-emerald-50/40',
  iconBg: 'bg-emerald-100',
  iconColor: 'text-emerald-600',
  ctaColor: 'text-emerald-700',
} as const;

const TONE_BLOCK = {
  surface: 'border-red-200 bg-red-50/40',
  iconBg: 'bg-red-100',
  iconColor: 'text-red-600',
  ctaColor: 'text-red-700',
} as const;
